// The venue's tape as a casino reads it: every settled position is a bet, its stake the margin
// (notional over leverage), its payout the stake plus the result, never below nothing. The
// relayer writes dollars as decimals; they become integers at eight places here, never floats.

import type { Trade } from "../sdk";
import { fmt, fromRaw, toRaw } from "../lib/num";

const PLACES = 8;

export type Settled = {
  seq: string;
  at: number;
  wallet: string;
  coin: string;
  instrumentId: number;
  isLong: boolean;
  /** Dollars at eight places. */
  stake: bigint;
  payout: bigint;
  bust: boolean;
};

const dollars = (text: string | null): bigint => toRaw(text ?? "0", PLACES) ?? 0n;

/** The bets the tape shows settled, newest first. */
export function settled(trades: Trade[]): Settled[] {
  const out: Settled[] = [];
  for (const trade of trades) {
    if (trade.kind === "open" || trade.leverage <= 0) continue;
    const stake = dollars(trade.notionalUsd) / BigInt(trade.leverage);
    if (stake <= 0n) continue;
    const bust = trade.kind === "liquidation";
    const after = stake + dollars(trade.pnlUsd);
    out.push({
      seq: trade.seq,
      at: trade.tsMs,
      wallet: trade.wallet,
      coin: trade.instrument,
      instrumentId: trade.instrumentId,
      isLong: trade.isLong,
      stake,
      payout: bust || after < 0n ? 0n : after,
      bust,
    });
  }
  return out;
}

/** `payout / stake` as `1.98×`, two places, toward zero. Any integers at one scale. */
export function multiple(payout: bigint, stake: bigint): string {
  if (stake <= 0n) return "0.00×";
  const hundredths = ((payout < 0n ? 0n : payout) * 100n) / stake;
  return `${hundredths / 100n}.${(hundredths % 100n).toString().padStart(2, "0")}×`;
}

/** Whether a bet paid at least 1.01× its stake: a win worth the gold. */
export const won = (payout: bigint, stake: bigint) => stake > 0n && payout * 100n >= stake * 101n;

/** Dollars at eight places for the screen. */
export const cash = (amount: bigint, compact = 7) => fmt(fromRaw(amount, PLACES), { places: 2, prefix: "$", compact });

/** Whole dollars at eight places, for a nameplate or the pot: `$152`, `$6,000`, `$10K`. */
export const chips = (amount: bigint) => fmt(fromRaw(amount, PLACES), { places: 0, prefix: "$", compact: 5 });
